import { Range } from "react-range"
import { playSliderToggle } from "@/utils/audioManager"
import classNames from "classnames"

interface CompactMoneySliderProps {
  value: number
  min: number
  max: number
  setValue: (value: number) => void
  disabled?: boolean
}

// A single-row money slider for tight spaces (used in the trade modal). It shows
// no value of its own — neither a floating label above the thumb nor an inline
// readout — because the one place the amount is written is the big field in the
// middle of the cash card. Two readouts a few pixels apart is how the amount
// stopped being part of the "A pays B" sentence in the first place.
const CompactMoneySlider: React.FC<CompactMoneySliderProps> = ({
  value,
  min,
  max,
  setValue,
  disabled,
}) => {
  if (max <= min) return null
  const clamped = Math.min(Math.max(value, min), max)
  const percent = ((clamped - min) / (max - min)) * 100
  const step = 10

  const commit = (v: number) => {
    const next = Math.min(Math.max(v, min), max)
    if (next === value) return next
    setValue(next)
    return next
  }

  const apply = (v: number) => {
    const before = value
    const next = commit(v)
    if (next !== before && !disabled) playSliderToggle()
  }

  // Size tracks --tc-rb so a very short trade panel can claw back a few px (see
  // useFitTradeCards); 34px is the roomy default everywhere else.
  const btnStyle = {
    width: "var(--tc-rb, 34px)",
    height: "var(--tc-rb, 34px)",
  }
  const btnClass = (enabled: boolean) =>
    classNames(
      "shrink-0 rounded-full flex items-center justify-center text-xl font-bold leading-none transition-all",
      {
        "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white active:scale-95":
          enabled,
        "bg-gray-700 text-gray-500 cursor-not-allowed": !enabled,
      }
    )

  // Everything on one compact row: [−] [slider] [+]. With the readout gone the
  // track gets the width the old input was taking, which is what makes dragging
  // to a particular amount practical in a sidebar this narrow.
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => apply(value - step)}
        disabled={disabled || value <= min}
        style={btnStyle}
        className={btnClass(!disabled && value > min)}
      >
        −
      </button>
      <div className="flex-1">
        <Range
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          values={[clamped]}
          onChange={(vals) => apply(vals[0])}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className={classNames("relative h-3 rounded-full w-full", {
                "bg-gray-600": disabled,
                "bg-gray-700": !disabled,
              })}
            >
              {!disabled && (
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 pointer-events-none"
                  style={{ width: `${percent}%` }}
                />
              )}
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              style={{ ...props.style }}
              className={classNames(
                "h-6 w-6 rounded-full border-2 shadow-sliderThumb",
                {
                  "bg-yellow-400 border-yellow-200": !disabled,
                  "bg-gray-700 border-gray-500": disabled,
                }
              )}
            />
          )}
        />
      </div>
      <button
        onClick={() => apply(value + step)}
        disabled={disabled || value >= max}
        style={btnStyle}
        className={btnClass(!disabled && value < max)}
      >
        +
      </button>
    </div>
  )
}

export default CompactMoneySlider
